import { AsyncActionButton, BackButton, ClosedApplicationTile, CustomerFeedbackDto, FeedbackOption, Loader, reportErrorToConsole, useCancelApplication, useCancelReasons, useGetCreditApplicationById, useSubmitFeedback } from '@applyonline/shared'
import { ECreditApplicationStatus, EDecision, EFinancingProgram } from '@if/core/types'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { extractNumbersFromMerchantId } from '../../data/merchant-selectors'
import { trackEvent } from '../../services/trackingEvents'
import { ETrackedEvents } from '../../types/tracking'
import AgentContactPage from './AgentContactPage'

const CancelApplicationPage = () => {
  const { t, i18n } = useTranslation()
  const params = useParams()
  const id = params.id!
  const navigate = useNavigate()

  const [withdrawalReasons, isLoadingReasons] = useCancelReasons(EFinancingProgram.Personal, i18n.language)
  const [selectedReason, setSelectedReason] = useState('')
  const [shouldAskToContactThem, setShouldAskToContactThem] = useState(false)
  const [contactConfirmation, setContactConfirmation] = useState(false)
  const [creditApp, isLoadingApp] = useGetCreditApplicationById(id, EFinancingProgram.Personal, false)
  const [cancelAppAsync, isCancelling] = useCancelApplication(EFinancingProgram.Personal)
  const [submitFeedback, isSubmittingFeedback] = useSubmitFeedback(EFinancingProgram.Personal)

  const hasCancelOrCompletedApplication =
    creditApp?.status === ECreditApplicationStatus.Cancelled || creditApp?.status === ECreditApplicationStatus.Completed

  const buildSelectedWithdrawalReasonFeedbackDto = useCallback(
    (contactClient: boolean): CustomerFeedbackDto => ({
      withdrawalReasonId: selectedReason,
      contactClient,
      isSatisfied: false,
      badReviewReasonId: '',
      creditApplicationId: id,
    }),
    [id, selectedReason],
  )

  const submitCancel = useCallback(
    (contactClient: boolean) => {
      if (creditApp) {
        const dto = buildSelectedWithdrawalReasonFeedbackDto(contactClient)
        const eventName =
          creditApp.normsDecision === null
            ? ETrackedEvents.WithdrewAfterPrequal
            : creditApp.finalDecision.decision === EDecision.ManualRevision
              ? ETrackedEvents.WithdrewManualRevision
              : ETrackedEvents.WithdrewAfterQualif

        trackEvent(eventName, creditApp)
        submitFeedback(dto)
          .then(() => cancelAppAsync(creditApp.id))
          .then(() => navigate(`/creditapplication/${creditApp.id}`))
          .catch(reportErrorToConsole)
      }
    },
    [buildSelectedWithdrawalReasonFeedbackDto, cancelAppAsync, creditApp, navigate, submitFeedback],
  )

  const handleSubmit = (event: { preventDefault: () => void }) => {
    if (withdrawalReasons.find((x) => x.id === selectedReason && x.contactClient === true)) {
      setShouldAskToContactThem(true)
    } else {
      submitCancel(false)
    }

    event.preventDefault()
  }

  const handleContactClientAnswered = useCallback(
    (contactClient: boolean) => {
      const dto = buildSelectedWithdrawalReasonFeedbackDto(contactClient)
      if (!contactClient) {
        submitCancel(false)
      } else {
        setContactConfirmation(true)
        submitFeedback(dto).catch(reportErrorToConsole)
      }
    },
    [buildSelectedWithdrawalReasonFeedbackDto, submitCancel, submitFeedback],
  )

  if (isLoadingApp || isLoadingReasons || !creditApp)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
  const creditAppStatus =
  (creditApp?.status as ECreditApplicationStatus.Completed | ECreditApplicationStatus.Cancelled) ?? ECreditApplicationStatus.Cancelled
  const merchantId = creditApp ? extractNumbersFromMerchantId(creditApp.merchantId) : ''
  const targetPath = `/${merchantId}`
  
  return (
    <main className="form-section">
      <form onSubmit={handleSubmit} style={{ textAlign: 'center' }}>
        {!shouldAskToContactThem &&
          !contactConfirmation &&
          !creditApp.applicantWantsToCancel &&
          !hasCancelOrCompletedApplication && (
            <>
              <div className="control-group radiobuttons-stacked-container">
                <h1>{t('customerFeedback.formTitle')}</h1>
                <div>
                  <p className="paragraph">{t('customerFeedback.formSubtitle')}</p>
                </div>
                {withdrawalReasons?.map((r) => (
                  <div className="radiobuttons-stacked-cards" key={r.id}>
                    <FeedbackOption id={r.id} text={r.text} selected={selectedReason} onChange={setSelectedReason} />
                  </div>
                ))}
              </div>
              <div className="btn-group mobile-reversed space-between">
                <BackButton />
                <AsyncActionButton type="submit" className="btn-blue" isPending={isSubmittingFeedback || isCancelling}>
                  <span>{t('common.submit')}</span>
                  <i className="fa-regular fa-arrow-right" />
                </AsyncActionButton>
              </div>
            </>
          )}
      </form>
      {shouldAskToContactThem &&
        !contactConfirmation &&
        !creditApp.applicantWantsToCancel &&
        !hasCancelOrCompletedApplication && (
          <div style={{ textAlign: 'center', marginTop: '30%' }}>
            <h1>{t('customerFeedback.confirmation')}</h1>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn-blue btn-xsmall"
                disabled={false}
                onClick={() => handleContactClientAnswered(true)}
              >
                {t('common.yes')}
              </button>
              <AsyncActionButton
                type="button"
                primary
                className="btn-xsmall"
                onClick={() => handleContactClientAnswered(false)}
                isPending={isSubmittingFeedback || isCancelling}
              >
                {t('common.no')}
              </AsyncActionButton>
            </div>
          </div>
        )}
      {(contactConfirmation || creditApp.applicantWantsToCancel) && !hasCancelOrCompletedApplication && (
        <AgentContactPage />
      )}
      {hasCancelOrCompletedApplication && <ClosedApplicationTile status={creditAppStatus} targetPath={targetPath} />}
    </main>
  )
}

export default CancelApplicationPage
